import { useState, useMemo, useEffect, useRef } from 'react';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { motion, AnimatePresence } from 'framer-motion';

import { useAuth } from '@/hooks/useAuth';
import { lastAttemptedLessonKey } from '@/lib/deviceProgressState';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LessonView } from '@/components/LessonView';
import { CustomLessonView } from '@/components/CustomLessonView';
import { BottomNav, type TabId } from '@/components/BottomNav';
import { HomeTab } from '@/components/HomeTab';
import { LearnTab } from '@/components/LearnTab';
import Profile from '@/pages/Profile';
import PlayBuilderPage from '@/pages/PlayBuilder';
import { PricingPage } from '@/components/PricingPage';
import { LeaderboardTab } from '@/components/LeaderboardTab';
import { ParentDashboard } from '@/components/ParentDashboard';
import { useProgress } from '@/hooks/useProgress';
import { findLesson, learningPaths, getNextLesson } from '@/data/lessons';
import { findSoccerLesson, getNextSoccerLesson } from '@/data/soccerLessons';
import { findBasketballLesson, getNextBasketballLesson, basketballLearningPaths } from '@/data/basketballLessons';
import { findBaseballLesson, getNextBaseballLesson } from '@/data/baseballLessons';
import { useAchievements } from '@/hooks/useAchievements';
import { usePreferredSport } from '@/hooks/usePreferredSport';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';
import { PremiumProvider, usePremium } from '@/hooks/usePremium';
import { UpgradeModal } from '@/components/UpgradeModal';
import { FeedbackButton } from '@/components/FeedbackButton';
import { LockedOverlay } from '@/components/PremiumGate';
import { OnboardingFlow } from '@/components/OnboardingFlow';
import { supabase } from '@/integrations/supabase/client';
import { ModeSwitcher } from '@/components/ModeSwitcher';
import { MilestoneProvider } from '@/components/milestones/MilestoneCelebration';
import { useMilestoneWatchers } from '@/hooks/useMilestoneWatchers';

function IndexInner() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('home');
  // Parent accounts (no player profile of their own) manage children, not
  // lessons — open them straight on the Parent Dashboard instead of Home.
  const parentTabApplied = useRef(false);
  const { isPremium, setShowUpgrade, setUpgradeSportName } = usePremium();
  const {
    progress,
    completeLesson,
    loseHeart,
    restoreOneHeart,
    recordQuestionResults,
    loaded: progressLoaded,
  } = useProgress();

  useAchievements(progress);
  useMilestoneWatchers(progress, progressLoaded);
  const { preferredSport, setSport, refetch: refetchSport } = usePreferredSport();
  const { isParent, isPlayer, playerProfile } = usePlayerProfiles();
  const [activeLessonId, setActiveLessonId] = useState<string | null>(null);
  

  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);

  // Process pending invite (auto-follow inviter after signup)
  useEffect(() => {
    if (!user) return;
    const raw = localStorage.getItem('pending_invite');
    if (!raw) return;
    try {
      const { code } = JSON.parse(raw);
      if (code) {
        supabase.rpc('follow_by_invite_code' as any, { p_code: code }).then(() => {
          localStorage.removeItem('pending_invite');
        });
      } else {
        localStorage.removeItem('pending_invite');
      }
    } catch {
      localStorage.removeItem('pending_invite');
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('onboarding_completed, username, display_name, primary_role')

      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          // Child player accounts and parent accounts never see the player
          // skill-level quiz — it personalises a curriculum they don't take.
          const isParentAccount = (data as any).primary_role === 'parent';
          if (!(data as any).onboarding_completed && !isPlayer && !isParentAccount) {
            setShowOnboarding(true);
          }
          setDisplayName((data as any).display_name || (data as any).username || null);
        }
        setOnboardingChecked(true);
      });
  }, [user, isPlayer]);

  // Land parents on their dashboard once, on first render after profiles load.
  useEffect(() => {
    if (parentTabApplied.current) return;
    if (!isParent || isPlayer) return;
    parentTabApplied.current = true;
    setActiveTab('players');
  }, [isParent, isPlayer]);



  const incompleteLessons = useMemo(() => {
    let count = 0;
    for (const path of learningPaths) {
      for (const unit of path.units) {
        if (progress.xp >= unit.requiredXp) {
          for (const lesson of unit.lessons) {
            if (!progress.completedLessons.includes(lesson.id)) count++;
          }
        }
      }
    }
    return count;
  }, [progress.completedLessons, progress.xp]);

  if (!onboardingChecked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Logo className="w-12 h-12 animate-pulse" />
      </div>
    );
  }

  if (showOnboarding) {
    return <OnboardingFlow onComplete={() => { refetchSport(); setShowOnboarding(false); }} />;
  }

  // Coach-authored lessons live in the database, not in the static curriculum.
  const isCustomActive = !!activeLessonId && activeLessonId.startsWith('custom:');

  const activeData = activeLessonId && !isCustomActive
    ? (findLesson(activeLessonId) || findSoccerLesson(activeLessonId) || findBasketballLesson(activeLessonId) || findBaseballLesson(activeLessonId))
    : null;
  const nextLesson = activeLessonId && !isCustomActive
    ? (getNextLesson(activeLessonId, progress.xp) || getNextSoccerLesson(activeLessonId, progress.xp) || getNextBasketballLesson(activeLessonId, progress.xp) || getNextBaseballLesson(activeLessonId, progress.xp))
    : null;

  if (activeLessonId && !isCustomActive && !activeData) {
    // Lesson not found in any sport's data — bail out with toast
    console.warn(`[Index] Lesson "${activeLessonId}" not found in any sport data`);
    setActiveLessonId(null);
  }

  if (isCustomActive && activeLessonId) {
    return (
      <CustomLessonView
        key={activeLessonId}
        lessonId={activeLessonId}
        onExit={() => setActiveLessonId(null)}
      />
    );
  }


  if (activeData) {
    return (
      <LessonView
        key={activeLessonId}
        lesson={activeData.lesson}
        hearts={progress.hearts}
        completedLessons={progress.completedLessons}
        nextLesson={nextLesson}
        onComplete={(correct, total, xp, qResults) => {
          completeLesson(activeData.lesson.id, correct, total, xp, qResults);
        }}
        onLoseHeart={loseHeart}
        onRestoreHeart={restoreOneHeart}
        onExit={() => setActiveLessonId(null)}
        onNextLesson={(id) => {
          try { localStorage.setItem(lastAttemptedLessonKey(user?.id, preferredSport), id); } catch {}
          setActiveLessonId(id);
        }}
      />
    );
  }

  const handleSelectLesson = (id: string) => {
    try { localStorage.setItem(lastAttemptedLessonKey(user?.id, preferredSport), id); } catch {}
    setActiveLessonId(id);
  };

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
  };

  // Player badge: show position if available
  const playerBadge = isPlayer && playerProfile ? (
    <span className="text-[9px] font-display font-bold uppercase tracking-widest text-accent bg-accent/10 px-1.5 py-0.5 rounded-md">
      {playerProfile.position?.toUpperCase() || playerProfile.sport}
    </span>
  ) : null;

  return (
    <div className="min-h-screen bg-background premium-page-bg text-foreground pb-20">
      <UpgradeModal />
      <FeedbackButton />

      

      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/70 backdrop-blur-md text-foreground border-b border-border">

        <div className="max-w-lg mx-auto px-5 py-3 flex items-center justify-between gap-3">
          {/* Left zone — Logo */}
          <div className="flex items-center gap-2 min-w-0">
            <Logo className="w-7 h-7 drop-shadow-sm" alt="Sportara" />
            <span className="font-display font-bold text-lg tracking-tight">
              Sportara
            </span>
          </div>


          {/* Right zone — Theme toggle + Username + Avatar */}
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              onClick={() => setActiveTab('profile')}
              className="flex items-center gap-2 hover:opacity-90 active:scale-95 transition-all duration-200"
            >
              <span className="hidden sm:inline text-sm font-display font-bold text-foreground/80">
                {displayName || user?.email?.split('@')[0] || ''}
              </span>

              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/25 to-primary/10 ring-1 ring-primary/20 flex items-center justify-center flex-shrink-0 shadow-md shadow-primary/20">
                <span className="text-xs font-display font-bold text-primary">
                  {(displayName || user?.email?.split('@')[0] || '?')[0].toUpperCase()}
                </span>
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Beta disclaimer — dismissible per session */}
      {!sessionStorage.getItem('beta_dismissed') && (
        <div className="bg-white/[0.03] backdrop-blur-md border-b border-white/10 text-center py-1.5 px-4 flex items-center justify-center gap-2">
          <p className="text-[11px] font-body text-foreground/60">
            You're using an early beta — content and features may change.
          </p>
          <button
            onClick={(e) => { sessionStorage.setItem('beta_dismissed', 'true'); (e.target as HTMLElement).closest('div')?.remove(); }}
            className="text-[10px] text-foreground/40 hover:text-foreground/70 transition-colors"
          >
            ✕
          </button>
        </div>
      )}

      <ModeSwitcher />

      <main className="max-w-lg mx-auto px-4 pt-3 pb-24">

        {activeTab === 'home' && (
          <RouteErrorBoundary section="Home">
            <HomeTab onSelectLesson={handleSelectLesson} onNavigate={handleTabChange} preferredSport={preferredSport} />
          </RouteErrorBoundary>
        )}
        {activeTab === 'learn' && (
          <RouteErrorBoundary section="Learn">
            <LearnTab onSelectLesson={handleSelectLesson} defaultSport={preferredSport} />
          </RouteErrorBoundary>
        )}
        {activeTab === 'leaderboard' && (
          <RouteErrorBoundary section="Leaderboard">
            <LeaderboardTab />
          </RouteErrorBoundary>
        )}
        {activeTab === 'pricing' && !isPlayer && (
          <RouteErrorBoundary section="Pricing">
            <PricingPage />
          </RouteErrorBoundary>
        )}
        {activeTab === 'profile' && (
          <RouteErrorBoundary section="Profile">
            <Profile />
          </RouteErrorBoundary>
        )}
        {activeTab === 'players' && (
          <RouteErrorBoundary section="Players">
            <ParentDashboard />
          </RouteErrorBoundary>
        )}
      </main>

      <BottomNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        // Parents don't take lessons themselves — the badge is noise for them.
        badges={{ learn: isParent && !isPlayer ? 0 : incompleteLessons }}
        showPlayersTab={isParent && !isPlayer}
        hidePricingTab={isPlayer}
      />

    </div>
  );
}

export default function Index() {
  return (
    <PremiumProvider>
      <MilestoneProvider>
        <IndexInner />
      </MilestoneProvider>
    </PremiumProvider>
  );
}
